/**
 * Левая панель: рейка команд и выдвижная панель выбранного раздела рядом с ней.
 * На телефоне раздел открывается окном снизу. Какие кнопки в рейке и что в разделе —
 * решает тот, кто её показывает: редактор плана, конструктор мебели.
 * Пустое место вокруг рейки и панели клики не ловит — они проходят в холст или сцену под ней.
 * `aside` — плашка рядом с панелью внизу (у «Отделки» — выбранное и его настройки), только на
 * широком экране: на телефоне раздел сам показывает это в окне.
 */
import type { CSSProperties, ReactNode } from "react";
import { DrawerShell } from "./DrawerShell";
import { Modal } from "./Modal";
import { ToolRail, type ToolRailItem } from "./ToolRail";

export const LeftPanel = ({
	items,
	disabled,
	labelsVisible,
	onToggleLabels,
	drawer,
	secondaryDrawer,
	onCloseDrawer,
	railKey,
	drawerClassName = "md:w-catalog-drawer",
	aside,
	style,
}: {
	items: ToolRailItem[];
	disabled?: boolean;
	labelsVisible: boolean;
	onToggleLabels: () => void;
	/** Открытый раздел: заголовок и содержимое; null — панель закрыта */
	drawer: { label: string; content: ReactNode; mobileOpen?: boolean } | null;
	/** Вторая карточка вплотную справа от основной (например, настройки выбранной секции или ячейки) */
	secondaryDrawer?: { label: string; content: ReactNode; onClose: () => void } | null;
	onCloseDrawer: () => void;
	/** Смена ключа пересобирает рейку (например, при смене вида редактора) */
	railKey?: string | number;
	/** Ширина выдвижной панели на десктопе */
	drawerClassName?: string;
	/** Плашка рядом с открытой панелью, прижата к низу */
	aside?: ReactNode;
	style?: CSSProperties;
}) => (
	<div
		className="pointer-events-none flex h-full min-h-0 items-start gap-2 *:pointer-events-auto"
		style={style}
	>
		<ToolRail
			key={railKey}
			items={items}
			disabled={disabled}
			labelsVisible={labelsVisible}
			onToggleLabels={onToggleLabels}
		/>
		{(drawer || secondaryDrawer) && (
			<>
				<div className="hidden h-full min-w-0 overflow-hidden rounded-lg border border-border bg-surface shadow-md md:flex">
					{drawer && (
						<div className={`h-full min-w-0 overflow-hidden ${drawerClassName}`}>
							<DrawerShell label={drawer.label} onClose={onCloseDrawer}>
								{drawer.content}
							</DrawerShell>
						</div>
					)}
					{secondaryDrawer && (
						<div
							className={`h-full min-w-0 overflow-hidden md:w-80 ${
								drawer ? "border-l border-border" : ""
							}`}
						>
							<DrawerShell label={secondaryDrawer.label} onClose={secondaryDrawer.onClose}>
								{secondaryDrawer.content}
							</DrawerShell>
						</div>
					)}
				</div>
				{aside && <div className="min-w-0 shrink self-end">{aside}</div>}
				{drawer && (drawer.mobileOpen ?? true) && (
					<Modal
						ariaLabel={drawer.label || "Панель инструментов"}
						className="h-2/3 max-h-modal overflow-hidden md:hidden"
						containerClassName="md:hidden"
						onClose={onCloseDrawer}
					>
						<DrawerShell
							label={secondaryDrawer ? secondaryDrawer.label : drawer.label}
							onClose={secondaryDrawer ? secondaryDrawer.onClose : onCloseDrawer}
						>
							{secondaryDrawer ? secondaryDrawer.content : drawer.content}
						</DrawerShell>
					</Modal>
				)}
			</>
		)}
	</div>
);
